import { useT } from "../i18n";
import { Monitor } from "lucide-react";

// "Download desktop app" dialog (spec §13).
//
// TEMPORARY (2026-06-18): the backend GitHub-Releases mirror is paused, so this dialog links
// directly to the locally-/CI-built artifacts served as static assets from /downloads. Windows
// and Linux are compiled locally (scripts/build-desktop.sh); macOS is built by the desktop-macos
// CI job and its artifact dropped into web/public/downloads. To restore the mirror-based flow
// (manifest from GET /api/v1/desktop/latest + authenticated blob download), revert this file.

// The desktop release version, single-sourced from package.json (Vite injects __APP_VERSION__).
// Keep package.json in lockstep with the desktop Cargo.toml via scripts/bump-version.sh.
const VERSION = __APP_VERSION__;

type OS = "win" | "mac" | "linux";

const ASSETS: { target: string; os: OS; label: string; href: string; sizeMB: number; note?: string }[] = [
  {
    target: "windows-x64",
    os: "win",
    label: "Windows — portable .exe",
    href: `/downloads/vault-desktop-${VERSION}.exe`,
    sizeMB: 25.7,
    note: "Just run it — no installation.",
  },
  {
    target: "macos-universal",
    os: "mac",
    label: "macOS (Intel + Apple Silicon) — .dmg",
    href: `/downloads/vault-desktop-${VERSION}.dmg`,
    sizeMB: 19,
    note: "Unsigned — first run: System Settings → Privacy & Security → Open Anyway.",
  },
  {
    target: "linux-appimage",
    os: "linux",
    label: "Linux — AppImage (universal)",
    href: `/downloads/vault-desktop-${VERSION}.AppImage`,
    sizeMB: 80.7,
    note: "chmod +x, then run. Self-contained.",
  },
  {
    target: "linux-deb",
    os: "linux",
    label: "Linux — .deb (Debian / Ubuntu)",
    href: `/downloads/vault-desktop-${VERSION}.deb`,
    sizeMB: 10.2,
    note: "sudo apt install ./vault-desktop-*.deb",
  },
];

function detectOS(): OS | "" {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/Windows/i.test(ua)) return "win";
  if (/Mac/i.test(ua)) return "mac";
  if (/Linux|X11|CrOS/i.test(ua)) return "linux";
  return "";
}

export function DownloadApp({ onClose }: { onClose: () => void }) {
  const t = useT();
  const myOS = detectOS();
  // Surface the visitor's platform first; keep the rest available for cross-platform testing.
  const assets = [...ASSETS].sort((a, b) => Number(b.os === myOS) - Number(a.os === myOS));

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60" onClick={onClose}>
      <div className="w-[540px] rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="text-muted"><Monitor className="h-4 w-4" /></span>
          <span className="font-medium">{t("Download desktop app")}</span>
          <span className="text-xs text-muted">v{VERSION}</span>
          <button onClick={onClose} className="ml-auto rounded border border-border px-2 py-1 text-xs hover:border-accent">
            {t("Close")}
          </button>
        </div>
        <div className="space-y-3 p-4 text-sm">
          <p className="text-muted">{t("Download the desktop client straight from the platform — no GitHub needed.")}</p>
          <div className="space-y-2">
            {assets.map((a) => {
              const mine = a.os === myOS;
              return (
                <a
                  key={a.target}
                  href={a.href}
                  download
                  className={`flex w-full items-start gap-3 rounded border px-3 py-2 text-left hover:border-accent ${
                    mine ? "border-accent bg-accent/5" : "border-border"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{a.label}</span>
                      {mine && <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] font-medium text-white">{t("Your OS")}</span>}
                    </div>
                    {a.note && <div className="mt-0.5 font-mono text-[11px] leading-snug text-muted">{a.note}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col items-end">
                    <span className="whitespace-nowrap text-xs text-accent">{t("Download")}</span>
                    <span className="whitespace-nowrap text-xs text-muted">{a.sizeMB.toFixed(1)} MB</span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
